// Block state model (Unity `BlocksModel.cs` / `Block.cs`, see
// `unity-overview.md` §3 "Blocks, infection, repair"). Pure logic: no WebGL,
// no DOM. The view subscribes to `blockChanged` to rebuild its mesh.

import type { EventBus } from "./events";
import { INFECTED_SECONDS } from "./defs";

/**
 * A block's life, as in the original (`Block.cs:5-11`):
 *
 *     healthy --touch--> infected --0.33s--> ghost --slice collapse--> empty
 *        ^                                     |                         |
 *        +---------------- repair -------------+-------------------------+
 *
 * - **`ghost` is what a destroyed block becomes** — a translucent shell that
 *   still has a surface bugs walk on. It is the endpoint of eating.
 * - **`empty` means the whole slice is gone.** A block reaches it only when an
 *   axis-slice's occupancy hits zero and the slice is swept
 *   (`BlocksModel.cs:204-211`). Nothing produces it yet — plan 13 owns slice
 *   collapse — so today it is reachable only by a test placing it by hand.
 * - Infection is **one touch**: `InfectBlock` returns early unless the block is
 *   `Healthy` (`BlocksModel.cs:494`), so a block cannot be infected twice and
 *   there is no accumulating meter. An earlier version of this port added 1/3
 *   per bite; that was an invention (plan 10 §3).
 */
export type BlockState = "healthy" | "infected" | "ghost" | "empty";

/** Integer grid coordinates of one block (00-architecture.md §7). */
export type Cell = readonly [number, number, number];

export interface BlocksModel {
  readonly size: number;
  /** State of a block; out-of-bounds coordinates read as `"empty"`. */
  state(x: number, y: number, z: number): BlockState;
  /** How far an infected block has decayed toward `ghost`, in [0, 1). */
  decay(x: number, y: number, z: number): number;
  /** Infect a healthy block; any other state is a no-op, as in the original. */
  infect(x: number, y: number, z: number): void;
  /** Restore to healthy; returns false if it already was. */
  repair(x: number, y: number, z: number): boolean;
  /**
   * Whether the block occupies space, and so hides what is behind it — the
   * original's `!Block.CanSeeThrough()` (`Block.cs:22-25`). A ghost is
   * see-through and does **not** occlude.
   */
  isSolid(x: number, y: number, z: number): boolean;
  /**
   * Whether a walker has something to stand on — the original's
   * `CheckPositionIsWalkable2` (`BugsModel.cs:2278-2295`), which rejects
   * **only** `Empty`. A ghost shell is floor; do not confuse this with
   * `isSolid`, which is a different question and the one plan 07 conflated.
   */
  isWalkable(x: number, y: number, z: number): boolean;
  /** Healthy blocks / total blocks — drives the ≤8% lose check. */
  healthyFraction(): number;
  /** Advance the decay of every infected block (`BlocksModel.Update`). */
  update(dt: number): void;
}

class Blocks implements BlocksModel {
  readonly size: number;
  private readonly states: BlockState[];
  /** Seconds left before an infected block turns to ghost; 0 when not infected. */
  private readonly countdown: Float64Array;
  /** Which blocks are currently infected, so `update` costs those, not 125. */
  private readonly infected = new Set<number>();
  private healthy: number;

  constructor(size: number, private readonly events: EventBus) {
    this.size = size;
    const count = size * size * size;
    this.states = new Array<BlockState>(count).fill("healthy");
    this.countdown = new Float64Array(count);
    this.healthy = count;
  }

  state(x: number, y: number, z: number): BlockState {
    const i = this.index(x, y, z);
    return i < 0 ? "empty" : this.states[i];
  }

  decay(x: number, y: number, z: number): number {
    const i = this.index(x, y, z);
    if (i < 0 || this.states[i] !== "infected") return 0;
    return 1 - this.countdown[i] / INFECTED_SECONDS;
  }

  infect(x: number, y: number, z: number): void {
    const i = this.index(x, y, z);
    // The original's guard: only a healthy block can be infected, so a second
    // bite on the same block does nothing at all (`BlocksModel.cs:494`).
    if (i < 0 || this.states[i] !== "healthy") return;
    this.countdown[i] = INFECTED_SECONDS;
    this.infected.add(i);
    this.setState(i, x, y, z, "infected");
  }

  repair(x: number, y: number, z: number): boolean {
    const i = this.index(x, y, z);
    if (i < 0) return false;
    this.countdown[i] = 0;
    this.infected.delete(i);
    if (this.states[i] === "healthy") return false;
    this.setState(i, x, y, z, "healthy");
    return true;
  }

  isSolid(x: number, y: number, z: number): boolean {
    const s = this.state(x, y, z);
    return s === "healthy" || s === "infected";
  }

  isWalkable(x: number, y: number, z: number): boolean {
    return this.state(x, y, z) !== "empty";
  }

  healthyFraction(): number {
    return this.healthy / this.states.length;
  }

  update(dt: number): void {
    if (this.infected.size === 0) return;
    // Collect first: `setState` mutates `this.infected`, and deleting from a
    // Set while iterating it is legal but reads as a trap waiting to happen.
    const due: number[] = [];
    for (const i of this.infected) {
      this.countdown[i] -= dt;
      if (this.countdown[i] <= 0) due.push(i);
    }
    for (const i of due) {
      const n = this.size;
      const x = i % n;
      const y = Math.floor(i / n) % n;
      const z = Math.floor(i / (n * n));
      this.countdown[i] = 0;
      this.infected.delete(i);
      this.setState(i, x, y, z, "ghost");
    }
  }

  private setState(i: number, x: number, y: number, z: number, state: BlockState): void {
    if (this.states[i] === state) return;
    // Mirrors `ChangeAmountOfHealthyBlocks`: the count drops the moment a block
    // stops being healthy, so infected->ghost does not decrement a second time.
    if (this.states[i] === "healthy") this.healthy--;
    else if (state === "healthy") this.healthy++;
    this.states[i] = state;
    this.events.emit("blockChanged", { x, y, z, state });
  }

  /** Linear index, or -1 when out of bounds. */
  private index(x: number, y: number, z: number): number {
    const n = this.size;
    if (x < 0 || x >= n || y < 0 || y >= n || z < 0 || z >= n) return -1;
    return (z * n + y) * n + x;
  }
}

export function createBlocks(size: number, events: EventBus): BlocksModel {
  return new Blocks(size, events);
}
